// Scripts pour la Page Produit

import { A, pipe } from "@mobily/ts-belt";
import { forEach as arrayForEach } from "@mobily/ts-belt/Array";
import { get as dictGet } from "@mobily/ts-belt/Dict";
import { tap as optionTap } from "@mobily/ts-belt/Option";
import { EitherAsync, Maybe } from "purify-ts";
import { match, P } from "ts-pattern";
import { type AnySchema, ValiError } from "valibot";

import type { WCStoreCart } from "./lib/types/api/cart";
import type { WCStoreCartAddItemArgs } from "./lib/types/api/cart-add-item.ts";
import type { FetchErrors } from "./lib/types/reseau";

import { ROUTE_API_AJOUTE_ARTICLE_PANIER } from "./constantes/api.ts";
import {
  ATTRIBUT_ARIA_SELECTED,
  ATTRIBUT_CHARGEMENT,
  ATTRIBUT_DESACTIVE,
  ATTRIBUT_HIDDEN,
  ATTRIBUT_INDEX_IMAGE_ACTIVE,
  ATTRIBUT_PRIX,
  SELECTEUR_BOUTON_AJOUT_PANIER,
  SELECTEUR_CONTENEUR_FLECHES_DEFILEMENT,
  SELECTEUR_FLECHE_DEFILEMENT_DROITE,
  SELECTEUR_FLECHE_DEFILEMENT_GAUCHE,
  SELECTEUR_IMAGE_COLONNE_GAUCHE,
  SELECTEUR_IMAGES_COLONNE_DROITE,
  SELECTEUR_LIENS_ONGLETS,
  SELECTEUR_PHOTOS_PRODUIT,
  SELECTEUR_PRIX_PRODUIT,
  SELECTEUR_SECTIONS_CONTENUS,
  SELECTEUR_SELECTEUR_QUANTITE,
} from "./constantes/dom";
import { lanceAnimationCycleLoading } from "./lib/animations.ts";
import { BadRequestError, reporteErreur, ServerError } from "./lib/erreurs.ts";
import { estHTMLSelectElement } from "./lib/gardes.ts";
import { emetMessageMajBoutonPanier } from "./lib/messages.ts";
import { creeReponseSimplifiee, eitherAsyncFetch, postBackend } from "./lib/reseau.ts";
import { WCStoreCartAddItemArgsSchema } from "./lib/schemas/api/cart-add-item.ts";
import { WCStoreCartSchema } from "./lib/schemas/api/cart.ts";
import { recupereElementDocumentEither, recupereEleOuLeve, recupereElesOuLeve } from "./lib/utils.ts";
import { eitherParse } from "./lib/validation.ts";

type EnsembleLienContenu = [HTMLAnchorElement, HTMLElement];
/** États utiles pour les scripts de la page. */
type EtatsPage = {
  /** L'ID en base de données du Produit. */
  idProduit: number;
  /** Un nonce pour l'authentification de requêtes API vers le backend WooCommerce. */
  nonce: string;
};

// @ts-expect-error -- États injectés par le modèle PHP
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- États injectés par le modèle PHP
const ETATS_PAGE: EtatsPage = _etats;

/**
 * Fonction utilitaire pour déplier toutes les sections de la Boîte des Informations Produit en ajustant les attributs
 * correspondants.
 */
const deplieToutesSections = (ensembleLiensContenus: Array<EnsembleLienContenu>): void =>
  arrayForEach(ensembleLiensContenus, (ensemble: EnsembleLienContenu): void => {
    Maybe.fromNullable(ensemble.at(0)).ifJust((e): void => e.setAttribute(ATTRIBUT_ARIA_SELECTED, "false"));
    Maybe.fromNullable(ensemble.at(1)).ifJust((e): void => e.setAttribute(ATTRIBUT_HIDDEN, "true"));
  });

// Éléments d'intérêt
const E = {
  BOUTON_AJOUT_PANIER: recupereEleOuLeve<HTMLButtonElement>(SELECTEUR_BOUTON_AJOUT_PANIER),
  CONTENEUR_FLECHES_DEFILEMENT: recupereEleOuLeve<HTMLDivElement>(SELECTEUR_CONTENEUR_FLECHES_DEFILEMENT),
  FLECHE_DEFILEMENT_DROITE: recupereEleOuLeve<HTMLButtonElement>(SELECTEUR_FLECHE_DEFILEMENT_DROITE),
  FLECHE_DEFILEMENT_GAUCHE: recupereEleOuLeve<HTMLButtonElement>(SELECTEUR_FLECHE_DEFILEMENT_GAUCHE),
  IMAGES: A.flat([
    recupereEleOuLeve<HTMLImageElement>(SELECTEUR_IMAGE_COLONNE_GAUCHE),
    recupereElesOuLeve<HTMLImageElement>(SELECTEUR_IMAGES_COLONNE_DROITE),
  ]),
  LIENS_ONGLETS: recupereElesOuLeve<HTMLAnchorElement>(SELECTEUR_LIENS_ONGLETS),
  PHOTOS_PRODUIT: recupereEleOuLeve<HTMLElement>(SELECTEUR_PHOTOS_PRODUIT),
  PRIX_PRODUIT: recupereEleOuLeve<HTMLParagraphElement>(SELECTEUR_PRIX_PRODUIT),
  SECTIONS_CONTENUS: recupereElesOuLeve<HTMLElement>(SELECTEUR_SECTIONS_CONTENUS),
  SELECTEUR_VARIATION: recupereElementDocumentEither<HTMLSelectElement>(SELECTEUR_SELECTEUR_QUANTITE),
};

const initialiseFlechesDefilementImages = (): void => {
  const recupereIndexImageCourante = (): number => Number(E.PHOTOS_PRODUIT.getAttribute(ATTRIBUT_INDEX_IMAGE_ACTIVE));

  // Affiche et cache en fonction de la position de défilement
  A.forEachWithIndex([E.IMAGES.at(0), E.IMAGES.at(-1)], (index, image): void => {
    if (!image) return;

    // Change les attributs gérants l'affichage des flèches de défilement
    new IntersectionObserver(
      A.forEach((entree): void =>
        match([entree.isIntersecting, index])
          .with([true, 0], (): void => E.CONTENEUR_FLECHES_DEFILEMENT.removeAttribute("data-images-presentes-debut"))
          .with([true, 1], (): void => E.CONTENEUR_FLECHES_DEFILEMENT.removeAttribute("data-images-presentes-fin"))
          .with([false, 0], (): void => E.CONTENEUR_FLECHES_DEFILEMENT.setAttribute("data-images-presentes-debut", ""))
          .with([false, 1], (): void => E.CONTENEUR_FLECHES_DEFILEMENT.setAttribute("data-images-presentes-fin", ""))
          .otherwise((): void => void 0)
      ),
      { root: null, rootMargin: "100% 0% 100% 0%", threshold: 0.5 },
    ).observe(image);

    // Change l'index de l'Image active en fonction du défilement
    A.forEachWithIndex(E.IMAGES, (index, image): void =>
      new IntersectionObserver(
        A.forEach((entree): void => {
          if (!entree.isIntersecting) return;
          E.PHOTOS_PRODUIT.setAttribute(ATTRIBUT_INDEX_IMAGE_ACTIVE, String(index));
        }),
        { root: null, rootMargin: "100% 0% 100% 0%", threshold: 0.5 },
      ).observe(image));

    // Défile au clic sur les flèches
    E.FLECHE_DEFILEMENT_GAUCHE.addEventListener(
      "click",
      (): void =>
        E.IMAGES[recupereIndexImageCourante() - 1]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        }),
    );
    E.FLECHE_DEFILEMENT_DROITE.addEventListener(
      "click",
      (): void =>
        E.IMAGES[recupereIndexImageCourante() + 1]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        }),
    );
  });
};

const gereBoiteInformationsProduit = (): void => {
  const onglets = new Map<string, EnsembleLienContenu>();

  // Créé la Map avec les ensembles Lien-Contenu
  E.LIENS_ONGLETS.forEach((lien, index): void => {
    const idOnglet: null | string = lien.getAttribute("id");
    const sectionCorrespondante: HTMLElement | undefined = E.SECTIONS_CONTENUS[index];

    if (!idOnglet) throw new Error("Le lien ne dispose pas d'ID !");
    if (!sectionCorrespondante) throw new Error("Le lien ne dispose pas de section correspondante !");

    onglets.set(idOnglet, [lien, sectionCorrespondante]);

    lien.addEventListener("click", (evenement: Event): void => {
      // Empêche la pollution de l'historique de navigation
      evenement.preventDefault();

      // Sauvegarde l'état d'ouverture de la section avant de toutes les fermer
      const estAncienOngletCourant: boolean = lien.getAttribute(ATTRIBUT_ARIA_SELECTED) === "true";
      pipe(onglets.values(), Array.from<EnsembleLienContenu>, deplieToutesSections);

      // Ne fais rien de plus si l'onglet sélectionné était le courant
      if (estAncienOngletCourant) return;

      // Ouvre le nouvel onglet sélectionné
      lien.setAttribute(ATTRIBUT_ARIA_SELECTED, "true");
      sectionCorrespondante.removeAttribute(ATTRIBUT_HIDDEN);
    });
  });

  // Ajoute des Écouteurs d'Événements
  E.SELECTEUR_VARIATION.ifRight((selecteur): void =>
    selecteur.addEventListener("change", (evenement: Event): void => {
      const cibleSelecteur: Maybe<HTMLSelectElement> = Maybe
        .fromNullable(evenement.target)
        .filter(estHTMLSelectElement);

      // Active le Bouton si le Sélecteur est valide
      cibleSelecteur
        .map(v => v.validity.valid)
        .ifJust((v: boolean) => E.BOUTON_AJOUT_PANIER.toggleAttribute(ATTRIBUT_DESACTIVE, !v));

      // Met à jour le prix affiché pour la Variation
      cibleSelecteur
        .chainNullable(s => s.selectedOptions[0])
        .chainNullable(e => e.getAttribute(ATTRIBUT_PRIX))
        .ifJust(p => {
          E.PRIX_PRODUIT.textContent = `${p}€`;
        });
    })
  );
  E.BOUTON_AJOUT_PANIER.addEventListener("click", (): void => ajouteProduitAuPanier());
};

const ajouteProduitAuPanier = (): void => {
  // Construis les arguments de la requête au backend
  const argsRequete: WCStoreCartAddItemArgs = {
    id: E.SELECTEUR_VARIATION
      .map((selecteur: HTMLSelectElement): number => Number(selecteur.value))
      // Récupère l'ID du Produit de la Page pour les Produits simples
      .orDefault(ETATS_PAGE.idProduit),
    quantity: 1,
  };

  // Réalise la Requête et traite sa Réponse
  void EitherAsync
    // 1. Valide les arguments de la Requête
    .liftEither(eitherParse(argsRequete, WCStoreCartAddItemArgsSchema))
    // 2. Exécute un Effet pour empêcher les requêtes concurrentes et lancer une animation de chargement
    .ifRight(() => {
      // Désactive le Bouton pour empêcher des requêtes concurrentes
      E.BOUTON_AJOUT_PANIER.setAttribute(ATTRIBUT_DESACTIVE, "");
      E.BOUTON_AJOUT_PANIER.setAttribute(ATTRIBUT_CHARGEMENT, "");

      // Lance un cycle d'animation sur le texte de chargement
      lanceAnimationCycleLoading(E.BOUTON_AJOUT_PANIER, 500);
    })
    // 3. Exécute la requête via fetch sous forme d'EitherAsync
    .chain((args: WCStoreCartAddItemArgs) =>
      eitherAsyncFetch(
        postBackend({
          corps: JSON.stringify(args),
          nonce: ETATS_PAGE.nonce,
          route: ROUTE_API_AJOUTE_ARTICLE_PANIER,
        }),
      )
    )
    // 4. Traite les cas d'Erreurs et récupère le Corps de la Réponse
    .chain((reponse: Response) =>
      EitherAsync<BadRequestError | ServerError, unknown>(async ({ throwE }) =>
        // Simplifie les données à matcher
        match(await creeReponseSimplifiee(reponse))
          .with({ status: 500 }, () => throwE(new ServerError("500 Server Error")))
          .with({ status: 400 }, () => throwE(new BadRequestError("400 Bad Request Error")))
          .with({ status: 201 }, r => r.body)
          .otherwise(erreur => throwE(new Error(`Erreur inconnue ${String(erreur.status)}`)))
      )
    )
    // 5. Vérifie le Schéma de la Réponse
    .chain((corpsReponse: unknown) => EitherAsync.liftEither(eitherParse(corpsReponse, WCStoreCartSchema)))
    // 6. Exécute un Effet pour la mise à jour du DOM avec les Résultats
    .ifRight((panier: WCStoreCart) =>
      pipe(
        dictGet(panier, "items_count"),
        optionTap((totalArticles: number) => {
          E.BOUTON_AJOUT_PANIER.textContent = "Added to cart!";
          emetMessageMajBoutonPanier({ quantiteProduits: totalArticles });
        }),
      )
    )
    .ifLeft(
      (erreur: BadRequestError | FetchErrors | ServerError | ValiError<AnySchema>) => {
        match(erreur)
          .with(P.instanceOf(ValiError), e => {
            reporteErreur(e);
            console.error(e.issues);
            // E.MESSAGE_ADRESSES.textContent = ERREUR_GENERIQUE_SOUMISSION_ADRESSES;
          })
          .with(P.instanceOf(ServerError), P.instanceOf(BadRequestError), e => {
            reporteErreur(e);
            console.error(e);
            // E.MESSAGE_ADRESSES.textContent = ERREUR_GENERIQUE_SOUMISSION_ADRESSES;
          })
          .with(P.instanceOf(DOMException), P.instanceOf(TypeError), P.instanceOf(Error), e => {
            reporteErreur(e);
            console.error(e);
            // E.MESSAGE_ADRESSES.textContent = ERREUR_GENERIQUE_RESEAU;
          })
          .exhaustive();

        E.BOUTON_AJOUT_PANIER.textContent = "Add to cart";
      },
    )
    .finally((): void => {
      // Désactive l'animation de chargement et rend le Bouton de nouveau cliquable
      E.BOUTON_AJOUT_PANIER.removeAttribute(ATTRIBUT_CHARGEMENT);
      E.BOUTON_AJOUT_PANIER.removeAttribute(ATTRIBUT_DESACTIVE);
    })
    .run();
};

document.addEventListener("DOMContentLoaded", (): void => {
  gereBoiteInformationsProduit();
  initialiseFlechesDefilementImages();
});
